import React from 'react';
import { theme } from '../../../shared/styles/core/theme';

const GAP_THRESHOLD = 3;
const AXIS_BREAK_AMPLITUDE = 5;
const AXIS_BREAK_HALFWAVE = 4;
const AXIS_BREAK_WAVES = 3;
const AXIS_BREAK_CLEAR_PAD = 3;

export interface AxisBreakGap {
    fromYear: number;
    toYear: number;
    breakMidpoint: number;
}

export interface ScaleMapping {
    toDisplay: Map<number, number>;
    toReal: Map<number, number>;
    displayTicks: number[];
    gaps: AxisBreakGap[];
}

const VISIBLE_GAP_UNITS = 2;

export const buildScaleMapping = (sortedYears: number[]): ScaleMapping => {
    const toDisplay = new Map<number, number>();
    const toReal = new Map<number, number>();
    const gaps: AxisBreakGap[] = [];

    if (sortedYears.length === 0) {
        return { toDisplay, toReal, displayTicks: [], gaps };
    }

    let shift = 0;
    toDisplay.set(sortedYears[0], sortedYears[0]);
    toReal.set(sortedYears[0], sortedYears[0]);

    for (let i = 1; i < sortedYears.length; i++) {
        const gapSize = sortedYears[i] - sortedYears[i - 1];

        if (gapSize > GAP_THRESHOLD) {
            shift += gapSize - VISIBLE_GAP_UNITS;
        }

        const displayPos = sortedYears[i] - shift;
        toDisplay.set(sortedYears[i], displayPos);
        toReal.set(displayPos, sortedYears[i]);

        if (gapSize > GAP_THRESHOLD) {
            const fromDisplay = toDisplay.get(sortedYears[i - 1])!;
            gaps.push({
                fromYear: sortedYears[i - 1],
                toYear: sortedYears[i],
                breakMidpoint: (fromDisplay + displayPos) / 2,
            });
        }
    }

    const displayTicks = sortedYears.map((y) => toDisplay.get(y)!);

    return { toDisplay, toReal, displayTicks, gaps };
};

export interface AxisBreakProps {
    xAxisMap?: Record<string, { scale: (value: number) => number }>;
    offset?: { top: number; height: number };
    gaps: AxisBreakGap[];
}

export const AxisBreakMarks: React.FC<AxisBreakProps> = ({ xAxisMap, offset, gaps }) => {
    if (!xAxisMap || !offset || gaps.length === 0) return null;

    const xAxis = Object.values(xAxisMap)[0];
    if (!xAxis?.scale) return null;

    const chartBottom = offset.top + offset.height;

    return (
        <g>
            {gaps.map(({ fromYear, toYear, breakMidpoint }) => {
                const midX = xAxis.scale(breakMidpoint);
                const totalWidth = AXIS_BREAK_WAVES * AXIS_BREAK_HALFWAVE * 2;
                const startX = midX - totalWidth / 2;

                const points: string[] = [];
                points.push(`${startX},${chartBottom}`);
                for (let i = 0; i < AXIS_BREAK_WAVES * 2 - 1; i++) {
                    const x = startX + (i + 1) * AXIS_BREAK_HALFWAVE;
                    const y = i % 2 === 0 ? chartBottom - AXIS_BREAK_AMPLITUDE : chartBottom + AXIS_BREAK_AMPLITUDE;
                    points.push(`${x},${y}`);
                }
                points.push(`${startX + totalWidth},${chartBottom}`);

                return (
                    <g key={`break-${fromYear}-${toYear}`}>
                        <rect
                            x={startX - AXIS_BREAK_CLEAR_PAD}
                            y={chartBottom - AXIS_BREAK_AMPLITUDE - AXIS_BREAK_CLEAR_PAD}
                            width={totalWidth + AXIS_BREAK_CLEAR_PAD * 2}
                            height={AXIS_BREAK_AMPLITUDE * 2 + AXIS_BREAK_CLEAR_PAD * 2}
                            fill={theme.colors.bg.primary}
                        />
                        <polyline
                            points={points.join(' ')}
                            fill="none"
                            stroke={theme.colors.text.secondary}
                            strokeLinejoin="round"
                            strokeLinecap="round"
                        />
                    </g>
                );
            })}
        </g>
    );
};
